import './App.scss';

import React, { useState, useEffect } from 'react';
import { ResizableBox } from 'react-resizable';
import { Button } from '@material-ui/core';
import ReactGA from 'react-ga4';

import {
  Options,
  Template,
  Map,
  Chart,
  Range,
  Progress,
  Header,
  Footer,
  Legend,
  EpisodeTimeline,
  EventNarrativePanel,
  Search,
  Help,
  Instructions,
  InfoModal,
  VersionBanner,
} from './Components';

import {
  getHazardData,
  getClimateData,
  getHazardNarrative,
  getClimateTrends,
  getHazardMapData,
  getClimateProjection,
} from './Utilities/Data';
import { useCurrentHeight, useCurrentWidth } from './Utilities/WindowSize';
import { attributeOptions } from './Utilities/Helpers';
import updateObject from './Utilities/updateObject';
import getNewColors from './Utilities/calcColors';
import downloadData from './Utilities/downloadData';

const btnStyle = {
  backgroundColor: 'red',
  border: '1px solid black',
  color: 'white',
  marginTop: '7px',
};

window.mobileCheck = function () {
  let check = false;
  (function (a) {
    if (
      // eslint-disable-next-line no-useless-escape
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
        a
      ) ||
      // eslint-disable-next-line no-useless-escape
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
        a.substr(0, 4)
      )
    )
      check = true;
  })(navigator.userAgent || navigator.vendor || window.opera);
  return check;
};

function App() {
  const [timeAt, setTimeAt] = useState(null);

  const [countyClimateData, setCountyClimateData] = useState({});
  const [countyClimateTrends, setCountyClimateTrends] = useState(null);
  const [countyColors, setCountyColors] = useState('rgba(0, 0, 0, 0)');
  const [countyHazardData, setCountyHazardData] = useState(null);
  const [countyHazardMapData, setCountyHazardMapData] = useState(null);
  const [currEmData, setCurrEmData] = useState({
    mean: [],
    area: { x: [], y: [] },
  });
  const [emissionHighData, setEmissionHighData] = useState({});
  const [emissionLowData, setEmissionLowData] = useState({});
  const [emissionMinMax, setEmissionMinMax] = useState([null, null]);
  const [episodes, setEpisodes] = useState({
    episodes: null,
    showEpisodes: false,
  });
  const [eventNarratives, setEventNarratives] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mapColorRange, setMapColorRange] = useState(null);
  const [modelTrendData, setModelTrendData] = useState(null);
  const [showVersion, setShowVersion] = useState(true);
  const [stateClimateData, setStateClimateData] = useState({});
  const [stateClimateTrends, setStateClimateTrends] = useState(null);
  const [stateColors, setStateColors] = useState('rgba(0, 0, 0, 0)');
  const [stateHazardData, setStateHazardData] = useState(null);
  const [stateHazardMapData, setStateHazardMapData] = useState(null);
  const [windowHeight, setWindowHeight] = useState(null);
  const [windowWidth, setWindowWidth] = useState(null);
  const [searchLocation, setSearchLocation] = useState({
    lat: null,
    lng: null,
    zoom: null,
    name: null,
  });
  const [options, setOptions] = useState({
    bigScreen: true,
    cbFriendly: false,
    chartTab: 0,
    chartVisible: false,
    climateOption: 'hvypcpn1',
    climateRange: {
      min: 1981,
      max: new Date().getFullYear() - 1,
      start: 1981,
      end: new Date().getFullYear() - 1,
    },
    hazardOptions: ['Heavy Rain', 'count'],
    hazardRange: {
      min: 1996,
      max: new Date().getFullYear(),
      start: 1996,
      end: new Date().getFullYear(),
    },
    infoModal: null,
    isCountyLevel: false,
    location: null,
    mode: true,
    screenByPass: false,
    showBadge: true,
    tab: 1,
    theme: 'Extreme Rainfall/Flooding',
  });

  // Hooks for getting screen width and height
  let width = useCurrentWidth();
  let height = useCurrentHeight();

  // Send analytics data to Google
  useEffect(() => {
    ReactGA.initialize('G-03N4DGP0XC');
    ReactGA.pageview(window.location.pathname + window.location.search);
  }, []);

  // Handles CSV download
  const handleDownloadData = () => {
    const { data, locationName, hYears } = chooseData(
      options.mode,
      options.location,
      options.climateOption
    );

    downloadData(
      options.mode,
      options.location,
      attributeOptions[options.climateOption],
      data,
      locationName,
      hYears,
      options.hazardOptions[0],
      options.hazardRange,
      emissionLowData,
      emissionHighData
    );
  };

  // Gets proper data for CSV download
  const chooseData = (isClimate, loc, climateOption) => {
    let data, hYears;

    let isState = loc.id.length === 2;
    let locationName = isState
      ? `${loc.name} State`
      : `${loc.name} County-${loc.state}`;

    if (isClimate) {
      if (isState) {
        data = stateClimateData[climateOption][loc.id];
        hYears = stateClimateData.years;
      } else {
        data = countyClimateData[climateOption][loc.id];
        hYears = countyClimateData.years;
      }
    } else {
      if (isState) {
        data = stateHazardData;
      } else {
        data = countyHazardData;
      }
    }

    return { data, locationName, hYears };
  };

  // Handles updating hazard data
  const updateHazardData = (opts) => {
    setLoading(true);
    Promise.all([
      getHazardData('State', opts.hazardOptions[0]),
      getHazardData('County', opts.hazardOptions[0]),
    ])
      .then((results) => {
        updateHazardMapData(...results, opts);

        if (episodes && episodes.year) {
          updateEpisodes(
            opts.location.id,
            opts.hazardOptions,
            results[0].episodes,
            results[1].episodes
          );
        }

        return results;
      })
      .then((results) => {
        setStateHazardData(results[0]);
        setCountyHazardData(results[1]);
        setLoading(false);
      })
      .catch(() => {
        alert(
          'An error has occured and the data being shown may not be accurate.'
        );
        setLoading(false);
      });
  };

  const updateHazardMapData = (sData, cData, opts) => {
    const state = getHazardMapData(
      sData,
      opts.hazardRange.start,
      opts.hazardRange.end
    );
    const county = getHazardMapData(
      cData,
      opts.hazardRange.start,
      opts.hazardRange.end
    );

    setStateHazardMapData(state);
    setCountyHazardMapData(county);

    updateColors(opts, state, county);
  };

  // Handles updating climate data
  const updateClimateData = (opts) => {
    setLoading(true);

    Promise.all([
      getClimateData(opts.climateOption, 'state_mean'),
      getClimateData(opts.climateOption, 'county_mean'),
    ])
      .then((results) => {
        let newState = {
          years: results[0]['years'],
          ...stateClimateData,
          [opts.climateOption]: results[0][opts.climateOption],
        };

        let newCounty = {
          years: results[1]['years'],
          ...countyClimateData,
          [opts.climateOption]: results[1][opts.climateOption],
        };

        return { newState, newCounty };
      })
      .then((newData) => {
        updateClimateTrends(newData.newState, newData.newCounty, opts);
        return newData;
      })
      .then((newData) => {
        setStateClimateData(newData.newState);
        setCountyClimateData(newData.newCounty);
        setLoading(false);
      })
      .catch(() => {
        alert(
          'An error has occured and the data being shown may not be accurate.'
        );
        setLoading(false);
      });
  };

  const updateClimateTrends = (sData, cData, opts) => {
    const state = getClimateTrends(
      sData,
      opts.climateRange.start,
      opts.climateRange.end,
      opts.climateRange.min
    );
    const county = getClimateTrends(
      cData,
      opts.climateRange.start,
      opts.climateRange.end,
      opts.climateRange.min
    );

    setStateClimateTrends(state);
    setCountyClimateTrends(county);

    updateColors(opts, state, county);
  };

  // Adjusts climateRange to account for winter
  const adjustClimateRange = (newOpts) => {
    if (attributeOptions[newOpts.climateOption]['winter']) {
      newOpts.climateRange = {
        ...newOpts.climateRange,
        min: 1982,
        start:
          newOpts.climateRange.start < 1982 ? 1982 : newOpts.climateRange.start,
      };
    } else {
      newOpts.climateRange = {
        ...newOpts.climateRange,
        min: 1981,
        start:
          newOpts.climateRange.start === 1982
            ? 1981
            : newOpts.climateRange.start,
      };
    }

    return newOpts;
  };

  // Handles updating episodes
  const updateEpisodes = async (id, hOptions, shData, chData) => {
    const year = episodes.year;

    let episodesList;
    if (id.length === 2) {
      episodesList = shData[id]
        ? shData[id].filter((arr) => arr[0] === year)
        : [];
    } else {
      episodesList = chData[id]
        ? chData[id].filter((arr) => arr[0] === year)
        : [];
    }

    if (episodesList.length > 0) {
      episodesList = episodesList[0][1];
    }

    let narratives = await getHazardNarrative(episodesList, hOptions);

    setEpisodes({ ...episodes, episodes: narratives });
  };

  // Handles updating map colors
  const updateColors = (opts, state, county) => {
    const colors = getNewColors(
      opts.cbFriendly,
      opts.mode,
      state,
      county,
      opts.climateOption,
      attributeOptions,
      opts.hazardOptions[1]
    );

    if (colors) {
      setMapColorRange(colors.newMCR);
      setStateColors(colors.newSC);
      setCountyColors(colors.newCC);
    }
  };

  // Handles updating model trend data
  const updateMTD = (data) => {
    if (data.upper?.x) {
      let emission = { mean: [], area: { x: [], y: [] } };
      let currYear = new Date().getFullYear();
      let mtd = {
        past: { mean: [], upper: [], lower: [], year: [] },
        future: { mean: [], upper: [], lower: [], year: [] },
      };

      data.upper.x.forEach((year, index) => {
        let nYear = parseInt(year);
        if (nYear > currYear - 1) {
          emission.mean.push(data.mean.y[index]);
          emission.area.x.push(year);
          emission.area.y.push([data.lower.y[index], data.upper.y[index]]);
        }

        let diff = currYear - nYear;
        if (diff <= 30 && diff > 0) {
          mtd['past']['mean'].push(data.mean.y[index]);
          mtd['past']['upper'].push(data.upper.y[index]);
          mtd['past']['lower'].push(data.lower.y[index]);
          mtd['past']['year'].push(year);
        } else if (diff > -30 && diff <= 0) {
          mtd['future']['mean'].push(data.mean.y[index]);
          mtd['future']['upper'].push(data.upper.y[index]);
          mtd['future']['lower'].push(data.lower.y[index]);
          mtd['future']['year'].push(year);
        }
      });

      setModelTrendData(mtd);
      setCurrEmData(emission);
    }
  };

  // Handles updating emission min and max
  const updateEmissionMinMax = (loc, climateOption, lowData, highData) => {
    if (loc && lowData.lower && highData.lower) {
      let history;
      try {
        history =
          loc.id.length === 2
            ? stateClimateData[climateOption][loc.id]
            : countyClimateData[climateOption][loc.id];
      } catch {
        history = [];
      }
      setEmissionMinMax([
        Math.min(...history, ...lowData.lower.y, ...highData.lower.y),
        Math.max(...history, ...lowData.upper.y, ...highData.upper.y),
      ]);
    }
  };

  // Handles updating emission high and low data
  const updateClimateProjection = async (opts) => {
    let data = await Promise.all(
      ['rcp45', 'rcp85'].map(async (emissionScenario) => {
        let series = await Promise.all(
          ['wmean', 'allmin', 'allmax'].map(async (grid) => {
            let result = await getClimateProjection(
              opts.location.id,
              opts.climateOption,
              grid,
              emissionScenario
            );
            return result;
          })
        );

        let data = { mean: series[0], lower: series[1], upper: series[2] };

        if (emissionScenario === 'rcp45') {
          setEmissionLowData(data);
        } else {
          setEmissionHighData(data);
        }

        return data;
      })
    );

    updateMTD(options.chartTab === 1 ? data[1] : data[0]);
    updateEmissionMinMax(opts.location, opts.climateOption, ...data);
  };

  // Handles triggering all side effects of altering the options obj.
  // // Using this instead of useEffects because all useEffects depending on options attributes would fire everytime due to options being set as a new object each time. This prevents infinite loops and allows for few props to be passed through
  const handleSideEffectUpdates = async (keys, newOpts) => {
    const hasHOs = keys.includes('hazardOptions');
    const hasCO = keys.includes('climateOption');
    const hasMode = keys.includes('mode');
    const hasLoc = keys.includes('location');
    const hasCR = keys.includes('climateRange');
    const hasHR = keys.includes('hazardRange');
    const hasTheme = keys.includes('theme');
    const hasCT = keys.includes('chartTab');
    const hasCBF = keys.includes('cbFriendly');

    if (hasLoc) {
      const newTime = new Date().getTime();

      if (options.location !== null) {
        ReactGA.event('time_at_location_seconds', {
          location_time: Math.round((newTime - timeAt) / 1000),
        });
      }

      const location_level =
        newOpts.location.id.length === 2 ? 'State' : 'County';

      const location_name =
        newOpts.location.id.length === 2
          ? `${newOpts.location.name} State`
          : `${newOpts.location.name} County-${newOpts.location.state}`;

      ReactGA.event('location_selected', {
        location_name,
        location_level,
      });

      setTimeAt(newTime);
    }

    if ((hasHOs || hasMode) && !newOpts.mode) {
      // If the impact is all that is changing, update colors but don't update the data
      if (
        newOpts.hazardOptions[0] === options.hazardOptions[0] &&
        newOpts.hazardOptions[1] !== options.hazardOptions[1]
      ) {
        if (newOpts.location) {
          updateEpisodes(
            newOpts.location.id,
            newOpts.hazardOptions,
            stateHazardData.episodes,
            countyHazardData.episodes
          );
        }
        updateColors(newOpts, stateHazardMapData, countyHazardMapData);
      } else {
        await updateHazardData(newOpts);
      }
    }

    if (!hasHOs && hasLoc && episodes && episodes.year) {
      updateEpisodes(
        newOpts.location.id,
        newOpts.hazardOptions,
        stateHazardData.episodes,
        countyHazardData.episodes
      );
    }

    if (
      hasCBF ||
      (hasMode &&
        newOpts.mode &&
        Object.keys(stateClimateTrends).includes(newOpts.climateOption))
    ) {
      updateColors(newOpts, stateClimateTrends, countyClimateTrends);
    }

    if (hasHOs || hasCO) {
      ReactGA.event('climate_or_hazard_selected', {
        risk_type: hasCO ? 'climate' : 'hazard',
        risk_hazard: hasCO
          ? attributeOptions[newOpts.climateOption]['name']
          : newOpts.hazardOptions[0],
        risk_metric: hasCO ? null : newOpts.hazardOptions[1],
      });
    }

    if (hasCO) {
      newOpts = adjustClimateRange(newOpts);

      if (!stateClimateData[newOpts.climateOption]) {
        updateClimateData(newOpts);
      }
    }

    if (hasCO || hasLoc) {
      updateEmissionMinMax(
        newOpts.location,
        newOpts.climateOption,
        emissionLowData,
        emissionHighData
      );
    }

    if (newOpts.mode && (hasCO || hasLoc) && newOpts.location?.id) {
      updateClimateProjection(newOpts);
    } else if (hasCT) {
      updateMTD(newOpts.chartTab === 1 ? emissionHighData : emissionLowData);
    }

    if (hasCR) {
      updateClimateTrends(stateClimateData, countyClimateData, newOpts);
    }

    if (hasHR) {
      updateHazardMapData(stateHazardData, countyHazardData, newOpts);
    }

    if ((hasLoc || hasTheme) && newOpts.tab !== 0) {
      newOpts.showBadge = true;
    }

    return newOpts;
  };

  // Handles changing options object
  const handleOptionsChange = async (value, path = 'multiple') => {
    let newOpts = JSON.parse(JSON.stringify(options));

    let keys = [];
    if (path === 'multiple' && value instanceof Array) {
      keys = value.map((arr) => {
        updateObject(newOpts, ...arr);
        return arr[1];
      });
    } else {
      updateObject(newOpts, value, path);
      keys.push(path);
    }

    newOpts = await handleSideEffectUpdates(keys, newOpts);

    setOptions(newOpts);
  };

  // Allows removing 'small screen' overlay on button click
  const byPassScreen = () => {
    handleOptionsChange([
      [true, 'screenByPass'],
      [true, 'bigScreen'],
    ]);
  };

  // Returns a sized timeline component
  const initTimeline = () => {
    let tWidth = (windowWidth - 156) / 4 + 24;
    let tHeight = windowHeight - 343;

    return (
      <ResizableBox
        id='slider'
        width={tWidth}
        height={tHeight}
        resizeHandles={['e']}
        axis={'x'}
        minConstraints={[tWidth, tHeight]}
        maxConstraints={[windowWidth - 70, windowHeight]}
      >
        <EpisodeTimeline
          episodes={episodes}
          hazardOption={options.hazardOptions[1]}
          loc={options.location}
          setEpisodes={setEpisodes}
          setEventNarratives={setEventNarratives}
        />
      </ResizableBox>
    );
  };

  // Handles updating screen size and triggering overlay for 'small screen'
  useEffect(() => {
    if (
      !options.screenByPass &&
      (width !== windowWidth || height !== windowHeight)
    ) {
      if (window.mobileCheck()) {
        console.log('test');
        handleOptionsChange('size', 'bigScreen');
      } else if (width < 1000 && height < 650) {
        handleOptionsChange('width and height', 'bigScreen');
      } else if (width < 1000) {
        handleOptionsChange('width', 'bigScreen');
      } else if (height < 650) {
        handleOptionsChange('height', 'bigScreen');
      } else {
        handleOptionsChange(true, 'bigScreen');
      }
    }

    setWindowWidth(width);
    setWindowHeight(height);
  }),
    [width, height];

  // Causes version banner to be shown for 10 seconds on mount
  useEffect(() => {
    const timeId = setTimeout(() => {
      setShowVersion(false);
    }, 10000);

    return () => {
      clearTimeout(timeId);
    };
  }, []);

  // Gets initial data
  useEffect(() => {
    updateClimateData(options);
  }, []);

  return (
    <>
      <div id='app'>
        <div id='infinity'></div>

        <div id='chart-exporting'></div>

        <Header />

        <div id='grid'>
          {loading && <Progress />}

          <Map
            climateOption={options.climateOption}
            countyClimateTrends={countyClimateTrends}
            countyColors={countyColors}
            countyHazardMapData={countyHazardMapData}
            hazardOptions={options.hazardOptions}
            isCountyLevel={options.isCountyLevel}
            mode={options.mode}
            range={options.mode ? options.climateRange : options.hazardRange}
            searchLocation={searchLocation}
            stateClimateTrends={stateClimateTrends}
            stateColors={stateColors}
            stateHazardMapData={stateHazardMapData}
            handleOptionsChange={handleOptionsChange}
          />

          <Options
            hazardOptions={options.hazardOptions}
            mode={options.mode}
            theme={options.theme}
            setEventNarratives={setEventNarratives}
            handleOptionsChange={handleOptionsChange}
          />

          <Range
            chartTab={options.chartTab}
            chartVisible={options.chartVisible}
            climateRange={options.climateRange}
            hazardRange={options.hazardRange}
            mode={options.mode}
            handleOptionsChange={handleOptionsChange}
          />

          <Legend
            mode={options.mode}
            mapColorRange={mapColorRange}
            climateOption={options.climateOption}
            hazardOptions={options.hazardOptions}
            isCountyLevel={options.isCountyLevel}
            cbFriendly={options.cbFriendly}
          />

          <Search handleSearchLocationChange={setSearchLocation} />

          <Help
            cbFriendly={options.cbFriendly}
            handleOptionsChange={handleOptionsChange}
          />

          {options.chartVisible && episodes.show ? (
            initTimeline()
          ) : (
            <Template
              chartTab={options.chartTab}
              chartVisible={options.chartVisible}
              climateOption={options.climateOption}
              climateRange={options.climateRange}
              countyClimateTrends={countyClimateTrends}
              countyHazardMapData={countyHazardMapData}
              hazardOptions={options.hazardOptions}
              hazardRange={options.hazardRange}
              loc={options.location}
              mode={options.mode}
              modelTrendData={modelTrendData}
              showBadge={options.showBadge}
              stateClimateTrends={stateClimateTrends}
              stateHazardMapData={stateHazardMapData}
              tab={options.tab}
              theme={options.theme}
              handleOptionsChange={handleOptionsChange}
            />
          )}

          {!options.location && <Instructions />}

          <Chart
            chartTab={options.chartTab}
            chartVisible={options.chartVisible}
            climateOption={options.climateOption}
            climateRange={options.climateRange}
            countyClimateData={countyClimateData}
            countyHazardData={countyHazardData}
            currEmData={currEmData}
            emissionMinMax={emissionMinMax}
            hazardOptions={options.hazardOptions}
            hazardRange={options.hazardRange}
            loc={options.location}
            mode={options.mode}
            stateClimateData={stateClimateData}
            stateHazardData={stateHazardData}
            windowHeight={windowHeight}
            windowWidth={windowWidth}
            downloadData={handleDownloadData}
            setEpisodes={setEpisodes}
            handleOptionsChange={handleOptionsChange}
          />

          {eventNarratives.length !== 0 && (
            <EventNarrativePanel
              eventNarratives={eventNarratives}
              hazardOptions={options.hazardOptions}
              loc={options.location}
              setEventNarratives={setEventNarratives}
            />
          )}
        </div>

        <Footer />

        <VersionBanner show={showVersion} setShowVersion={setShowVersion} />

        {options.infoModal && (
          <InfoModal
            infoModal={options.infoModal}
            handleOptionsChange={handleOptionsChange}
          />
        )}
      </div>

      {options.bigScreen !== true && (
        <div id='too-small'>
          <div>
            This tool was designed to be used with a larger display or window
            size than you are currently using. For the best experience, please
            increase the {options.bigScreen} of your browser window or view the
            tool on a larger device.
            <br></br>
            <Button onClick={byPassScreen} style={btnStyle}>
              Continue Anyway
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

export default App;
